import { AlertTriangle, ArrowRight, CheckCircle2, Clock, Factory, Gauge, TrendingDown, TrendingUp, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAgentStore, type AgentWidget } from '@/agent/agentStore';
import { useAgentBridge } from '@/agent/AgentBridgeProvider';
import { simulateAgentResponse } from '@/agent/simulateResponse';

interface WidgetProps {
  widget: AgentWidget;
  messageId: string;
}

export function AgentWidgetRenderer({ widget, messageId }: WidgetProps) {
  switch (widget.type) {
    case 'kpi_grid':
      return <KpiGridWidget widget={widget} />;
    case 'oee_gauge':
      return <OeeGaugeWidget widget={widget} />;
    case 'nc_list':
      return <NcListWidget widget={widget} />;
    case 'wo_card':
      return <WoCardWidget widget={widget} />;
    case 'confirm':
      return <ConfirmWidget widget={widget} messageId={messageId} />;
    case 'action_buttons':
      return <ActionButtonsWidget widget={widget} />;
    default:
      return null;
  }
}

// ------------------------------------------------------------
// KPI grid
// ------------------------------------------------------------
function KpiGridWidget({ widget }: { widget: Extract<AgentWidget, { type: 'kpi_grid' }> }) {
  return (
    <div className="mt-2 rounded-md border bg-background/50 p-2">
      {widget.title && <div className="text-xs font-medium text-muted-foreground mb-1.5">{widget.title}</div>}
      <div className="grid grid-cols-2 gap-1.5">
        {widget.items.map((kpi, i) => {
          const toneClass =
            kpi.tone === 'success'
              ? 'text-[hsl(var(--success))]'
              : kpi.tone === 'warning'
                ? 'text-[hsl(var(--warning))]'
                : kpi.tone === 'danger'
                  ? 'text-destructive'
                  : 'text-foreground';
          const Trend = kpi.delta?.startsWith('-') ? TrendingDown : TrendingUp;
          return (
            <div key={i} className="rounded bg-card border px-2 py-1.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground leading-tight">{kpi.label}</div>
              <div className={`text-base font-semibold leading-tight ${toneClass}`}>{kpi.value}</div>
              {kpi.delta && (
                <div className="text-[10px] text-muted-foreground flex items-center gap-0.5 mt-0.5">
                  <Trend className="h-2.5 w-2.5" />
                  {kpi.delta}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// OEE gauge
// ------------------------------------------------------------
function OeeGaugeWidget({ widget }: { widget: Extract<AgentWidget, { type: 'oee_gauge' }> }) {
  const oeeTone =
    widget.oee >= 85 ? 'text-[hsl(var(--success))]' : widget.oee >= 70 ? 'text-[hsl(var(--warning))]' : 'text-destructive';
  return (
    <div className="mt-2 rounded-md border bg-background/50 p-2.5">
      <div className="flex items-center gap-2 mb-2">
        <Gauge className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-medium">OEE {widget.line ?? 'Línea A'}</span>
        <span className={`ml-auto text-xl font-bold ${oeeTone}`}>{widget.oee.toFixed(1)}%</span>
      </div>
      <div className="space-y-1.5">
        <KpiRow label="Disponibilidad" value={widget.availability} />
        <KpiRow label="Rendimiento" value={widget.performance} />
        <KpiRow label="Calidad" value={widget.quality} />
      </div>
    </div>
  );
}

function KpiRow({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="flex justify-between text-[11px] text-muted-foreground mb-0.5">
        <span>{label}</span>
        <span className="font-medium text-foreground">{value.toFixed(1)}%</span>
      </div>
      <Progress value={value} className="h-1" />
    </div>
  );
}

// ------------------------------------------------------------
// NC list
// ------------------------------------------------------------
function NcListWidget({ widget }: { widget: Extract<AgentWidget, { type: 'nc_list' }> }) {
  const { executeActions } = useAgentBridge();
  const sevColor = (s: string) =>
    s === 'CRITICAL' ? 'bg-destructive/15 text-destructive border-destructive/30'
      : s === 'MAJOR' ? 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30'
      : 'bg-muted text-muted-foreground border-border';
  return (
    <div className="mt-2 rounded-md border bg-background/50 p-2 space-y-1.5">
      {widget.items.map((nc) => (
        <button
          key={nc.id}
          onClick={() =>
            executeActions([
              { type: 'navigate', to: '/quality/board' },
              { type: 'select_item', view: 'quality_board', id: nc.id },
            ])
          }
          className="w-full text-left rounded border bg-card hover:bg-accent/50 transition-colors px-2 py-1.5 flex items-start gap-2"
        >
          <Badge variant="outline" className={`text-[10px] h-4 px-1 shrink-0 ${sevColor(nc.severity)}`}>
            {nc.severity}
          </Badge>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{nc.id} · {nc.title}</div>
            <div className="text-[10px] text-muted-foreground flex items-center gap-2 mt-0.5">
              {nc.line && <span className="flex items-center gap-0.5"><Factory className="h-2.5 w-2.5" />{nc.line}</span>}
              {nc.ageMin != null && <span className="flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{nc.ageMin}m</span>}
            </div>
          </div>
          <ArrowRight className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------
// WO mini-card
// ------------------------------------------------------------
function WoCardWidget({ widget }: { widget: Extract<AgentWidget, { type: 'wo_card' }> }) {
  const { executeActions } = useAgentBridge();
  return (
    <div className="mt-2 rounded-md border bg-background/50 p-2.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate">{widget.id}</div>
          <div className="text-[10px] text-muted-foreground truncate">{widget.product}</div>
        </div>
        {widget.status && (
          <Badge variant="outline" className="text-[10px] h-4 px-1 shrink-0">{widget.status}</Badge>
        )}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
        <span>Progreso</span>
        <span className="font-medium text-foreground">{widget.progress}%</span>
      </div>
      <Progress value={widget.progress} className="h-1 mb-2" />
      {widget.dueAt && (
        <div className="text-[10px] text-muted-foreground flex items-center gap-1 mb-2">
          <Clock className="h-2.5 w-2.5" /> Entrega: {widget.dueAt}
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        className="h-6 w-full text-[11px]"
        onClick={() =>
          executeActions([
            { type: 'navigate', to: '/production/dispatch' },
            { type: 'select_item', view: 'dispatch_board', id: widget.id },
            { type: 'highlight', selector: `[data-id="${widget.id}"]`, durationMs: 2500 },
          ])
        }
      >
        Ver en Dispatch <ArrowRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}

// ------------------------------------------------------------
// Confirm widget (inline)
// ------------------------------------------------------------
function ConfirmWidget({
  widget,
  messageId,
}: {
  widget: Extract<AgentWidget, { type: 'confirm' }>;
  messageId: string;
}) {
  const updateAgentMessage = useAgentStore((s) => s.updateAgentMessage);
  const messages = useAgentStore((s) => s.messages);
  const { executeActions } = useAgentBridge();

  const resolve = (resolution: 'confirmed' | 'cancelled') => {
    const msg = messages.find((m) => m.id === messageId);
    if (!msg) return;
    const widgets = (msg.widgets ?? []).map((w) =>
      w.type === 'confirm' && w.token === widget.token ? { ...w, resolved: resolution } : w,
    );
    updateAgentMessage(messageId, { widgets });

    if (resolution === 'confirmed') {
      // Trigger a toast + send a follow-up message to the brain
      void executeActions([{ type: 'toast', message: 'Acción confirmada', level: 'success' }]);
      useAgentStore.getState().sendUserMessage(`✓ Confirmado: ${widget.token}`);
      void simulateAgentResponse(`__confirmed__:${widget.token}`, executeActions);
    } else {
      void executeActions([{ type: 'toast', message: 'Acción cancelada', level: 'info' }]);
    }
  };

  if (widget.resolved) {
    const isOk = widget.resolved === 'confirmed';
    return (
      <div className={`mt-2 rounded-md border p-2 flex items-center gap-2 text-xs ${isOk ? 'bg-[hsl(var(--success))]/10 border-[hsl(var(--success))]/30' : 'bg-muted'}`}>
        {isOk ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> : <XCircle className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className={isOk ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}>
          {isOk ? 'Confirmado' : 'Cancelado'}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border bg-[hsl(var(--warning))]/10 border-[hsl(var(--warning))]/30 p-2">
      <div className="flex items-start gap-2 mb-2">
        <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))] mt-0.5 shrink-0" />
        <div className="text-xs text-foreground">{widget.prompt}</div>
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" className="h-7 text-[11px] flex-1" onClick={() => resolve('confirmed')}>
          {widget.confirmLabel ?? 'Confirmar'}
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] flex-1" onClick={() => resolve('cancelled')}>
          {widget.cancelLabel ?? 'Cancelar'}
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Action buttons (quick replies)
// ------------------------------------------------------------
function ActionButtonsWidget({ widget }: { widget: Extract<AgentWidget, { type: 'action_buttons' }> }) {
  const { executeActions } = useAgentBridge();
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {widget.items.map((btn, i) => (
        <Button
          key={i}
          size="sm"
          variant={btn.variant ?? 'outline'}
          className="h-7 text-[11px]"
          onClick={() => {
            useAgentStore.getState().sendUserMessage(btn.sendText);
            void simulateAgentResponse(btn.sendText, executeActions);
          }}
        >
          {btn.label}
        </Button>
      ))}
    </div>
  );
}
